import { useCallback, useRef, useState } from 'react';
import { useFragment } from '@apollo/client';
import {
  useIsAssetLifecycleManagementEnabled,
  useProjectAssetLifecyclePolicy,
  useWorkspaceAssetLifecyclePolicy,
} from '@frameio/content-security';
import type {
  MountedStorageStatus,
  ProjectSettingsFieldsFragment,
  UpdateProjectInput,
} from '@frameio/graphql-client/generated';
import {
  ProjectSettingsFields,
  WorkspaceInfoBasic,
} from '@frameio/graphql-client/generated';
import {
  useProjectPermissions,
  useWorkspacePermissions,
} from '@frameio/permissions';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsMountableStorageEnabled } from '@frameio/shared/plans/hooks/useIsMountableStorageEnabled';
import { useUpdateProject } from '@frameio/shared/resources';
import {
  DisableMountedStorageDialog,
  MountedStorageTypeSelect,
  useAccountStorageInfo,
  useMountedStorageStatus,
} from '@frameio/shared/resources/mounted-storage';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import {
  Box,
  Button2,
  Dialog,
  DialogActions,
  DialogHeader,
  Separator,
  theme,
  useActiveWindowBreakpoint,
  useMeasure,
} from '@frameio/vapor';
import { useGetWorkspacesInfo } from '../../shared/hooks';
import TEXT from './generated/TEXT/ProjectSettingsModal';
import { ProjectAccessSwitch } from './ProjectAccessSwitch';
import { ProjectAssetPolicyToggle } from './ProjectAssetPolicyToggle';
import { ProjectAvatarEditor } from './ProjectAvatarEditor';
import { ProjectSettingsSection } from './ProjectSettingsSection';
import { ContentColumn, ContentContainer, FooterContainer } from './styles';
import { WorkspacePicker } from './WorkspacePicker';

TEXT.DEFINE({
  /** Dialog title for project settings */
  projectSettings: 'Project settings',
  /* Aria label for project name settings */
  projectName: 'Project Name',
  /** Fallback project name when none is set */
  defaultProjectName: 'Untitled Project',
  /** Label for the avatar editor action */
  editAvatar: 'Edit project avatar',
  /** Secondary action label to cancel edits */
  cancel: 'Cancel',
  /** Primary action label to save changes */
  save: 'Save',
  /** Accessible label for the dialog close button */
  dialogCloseLabel: 'Close Dialog',
  /** Section title for project access and policy settings */
  accessSectionTitle: 'Access',
  /** Section title for project storage settings */
  storageSectionTitle: 'Storage',
  moveProject: {
    /** Title for the confirm dialog when moving a project to standard storage */
    title: 'Move Project to Standard Storage',
    /** Confirm button label for the move project dialog */
    buttonText: 'Move Project',
    /** Confirmation word the user must type to proceed */
    confirmationWord: 'move',
  },
});

export const screenVariants = {
  base: {
    dialogWidth: '100%',
    pickerWidth: '100%',
  },
  tablet: {
    dialogWidth: 620,
    pickerWidth: 280,
  },
  desktop: {
    dialogWidth: 620,
    pickerWidth: 280,
  },
};

type PerformSaveFunction = () => void;

export type ProjectSettingsModalProps = {
  isShowing: boolean;
  onRequestClose: () => void;
  accountId: string;
  projectId: string;
  trackingSource: TrackingSourceType;
  workspaceId: string;
  zIndex?: number;
  onRequestStorageConfirm?: (performSave: PerformSaveFunction) => void;
};

const ProjectSettingsModalContent = ({
  isShowing,
  onRequestClose,
  accountId: __accountId,
  projectId: projectIdProp,
  trackingSource,
  workspaceId,
  onRequestStorageConfirm,
}: ProjectSettingsModalProps) => {
  // prevent the projectId becoming null while the modal is closing
  const projectIdRef = useRef(projectIdProp);
  const projectId = !isShowing ? projectIdRef.current : projectIdProp;
  const { data: project } = useFragment<
    ProjectSettingsFieldsFragment,
    undefined
  >({
    fragment: ProjectSettingsFields,
    fragmentName: 'ProjectSettingsFields',
    from: {
      __typename: 'Project',
      id: projectId,
    },
  });
  const updateProject = useUpdateProject();
  const { shouldDisableUpgradeToMountedStorage } = useAccountStorageInfo();
  const isWorkfront = !!project?.isWorkfront;
  const [hasAvatarBeenDeleted, setHasAvatarBeenDeleted] = useState(false);
  const [name, setName] = useState<string>(
    project?.name ?? TEXT.defaultProjectName
  );
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const avatarThumbRef = useRef<string | null>(null);
  const avatarThumb = avatarThumbRef.current ?? project?.images?.image256;
  const avatarOriginal = hasAvatarBeenDeleted
    ? null
    : avatarThumb ?? project?.images?.original;
  const [isRestricted, setIsRestricted] = useState(project?.private ?? false);
  const projectBytes = project?.storage ?? 0;
  // determine if MountedStorageTypeSelect should be enabled to allow upgrade
  // from standard to mounted storage (DISABLED => ENABLED) based on the
  // remaining mounted storage bytes available in the account
  const storageUpgradeError =
    project?.mountedStorageStatus === 'DISABLED' &&
    shouldDisableUpgradeToMountedStorage(projectBytes);

  const handleToggleIsRestricted = useCallback(
    () => setIsRestricted((prev) => !prev),
    []
  );

  const handleAvatarSave = useCallback(
    (blob: Blob) => {
      setAvatarBlob(blob);
      avatarThumbRef.current = blob ? URL.createObjectURL(blob) : null;
      if (project?.images?.original && blob === null) {
        setHasAvatarBeenDeleted(true);
      } else {
        setHasAvatarBeenDeleted(false);
      }
    },
    [project?.images?.original]
  );

  const breakpoint = useActiveWindowBreakpoint();
  const [ref, bounds] = useMeasure();
  const inputMeasure =
    breakpoint === 'base'
      ? bounds.width
      : screenVariants[breakpoint].pickerWidth;

  const { data: projectWorkspace } = useFragment({
    fragment: WorkspaceInfoBasic,
    fragmentName: 'WorkspaceInfoBasic',
    from: {
      __typename: 'Workspace',
      id: workspaceId,
    },
  });

  const accountId = useActiveAccountId();

  useGetWorkspacesInfo({ accountId });

  const canManageProjectResourceControlPolicies = useProjectPermissions({
    projectId,
    check: 'canManageResourceControlPolicies',
  });

  const canManageWorkspaceResourceControlPolicies = useWorkspacePermissions({
    check: 'canManageResourceControlPolicies',
    workspaceId,
  });

  const workspacePolicy = useWorkspaceAssetLifecyclePolicy(workspaceId);
  const lifecyclePolicy = useProjectAssetLifecyclePolicy(projectId);
  const [lifecycleExemption, setLifecycleExemption] = useState<boolean | null>(
    null
  );
  const isALMEnabled = useIsAssetLifecycleManagementEnabled();
  const showALMToggle =
    isALMEnabled &&
    canManageWorkspaceResourceControlPolicies &&
    workspacePolicy.enabled;

  const isMountedStorageViewable = useIsMountableStorageEnabled();
  const { isAccountEnabled: isMountedStorageEnabled } =
    useMountedStorageStatus(projectId);
  const showStorageSection =
    isMountedStorageViewable &&
    isMountedStorageEnabled &&
    project.mountedStorageStatus !== 'LOCKED';
  const [mountedStorageStatus, setMountedStorageStatus] =
    useState<MountedStorageStatus>(project.mountedStorageStatus ?? 'LOCKED');
  const isSwitchingToStandardStorage =
    project.mountedStorageStatus === 'ENABLED' &&
    mountedStorageStatus === 'DISABLED';

  const handleCancel = useCallback(() => {
    avatarThumbRef.current = null;
    onRequestClose();
  }, [onRequestClose]);

  const performSave = useCallback(() => {
    const description = project?.description ?? '';
    const input: UpdateProjectInput = {
      id: projectId,
      description,
      name,
      private: isRestricted ?? false,
    };

    if (showStorageSection) {
      input.mountedStorageStatus = mountedStorageStatus;
    }

    if (showALMToggle) {
      input.ignoreArchive = lifecycleExemption ?? lifecyclePolicy.isExempt;
    }

    updateProject(input, avatarBlob, hasAvatarBeenDeleted, {
      projectNameHasChanged: project.name !== name,
      projectVisibilityHasChanged: project.private !== isRestricted,
      trackingSource,
    });

    avatarThumbRef.current = null;
    onRequestClose();
  }, [
    project?.description,
    project.name,
    project.private,
    projectId,
    name,
    isRestricted,
    showStorageSection,
    showALMToggle,
    updateProject,
    avatarBlob,
    hasAvatarBeenDeleted,
    trackingSource,
    onRequestClose,
    mountedStorageStatus,
    lifecycleExemption,
    lifecyclePolicy.isExempt,
  ]);

  const handleSave = useCallback(() => {
    if (isSwitchingToStandardStorage && onRequestStorageConfirm) {
      onRequestStorageConfirm(performSave);
      return;
    }
    performSave();
  }, [isSwitchingToStandardStorage, onRequestStorageConfirm, performSave]);

  const isSaveDisabled = name.trim() === '';
  const canEditUserPermissions = useWorkspacePermissions({
    check: 'canEditUserPermissions',
    workspaceId,
  });

  return (
    <>
      <DialogHeader
        closeLabel={TEXT.dialogCloseLabel}
        showBottomBorder={false}
        title={TEXT.projectSettings}
      />
      <Box
        height="100%"
        style={{
          overflowY: 'scroll',
          borderBottom: `1px solid ${theme.colors.border.secondary}`,
          borderTop: `1px solid ${theme.colors.border.secondary}`,
        }}
      >
        <ContentContainer
          desktopPickerWidth={screenVariants.desktop.pickerWidth}
          tabletPickerWidth={screenVariants.tablet.pickerWidth}
        >
          <ContentColumn>
            <label aria-label={TEXT.projectName}>
              <ProjectAvatarEditor
                avatarOriginal={avatarOriginal}
                avatarThumb={avatarThumb}
                isDeleteAvatar={hasAvatarBeenDeleted}
                isRestricted={isRestricted}
                isWorkfront={isWorkfront}
                name={name}
                nameInputDataTestId="workspaces-project-settings--modal--name-field"
                onNameChange={setName}
                onSave={handleAvatarSave}
                shouldFocusOnMount={!isWorkfront}
              />
            </label>
          </ContentColumn>
          <ContentColumn
            ref={ref}
            grow={true}
            style={{ gap: theme.spaces[12] }}
          >
            {(canEditUserPermissions || showALMToggle) && (
              <ProjectSettingsSection title={TEXT.accessSectionTitle}>
                {canEditUserPermissions && (
                  <ProjectAccessSwitch
                    canManageAccess={!isWorkfront}
                    isLastToggle={!workspacePolicy.enabled}
                    isRestricted={isRestricted}
                    onChange={handleToggleIsRestricted}
                    showIsRestrictedDescription={true}
                  />
                )}
                {showALMToggle && (
                  <>
                    <Box backgroundColor="card">
                      <Separator
                        color="primary"
                        height={1}
                        style={{ margin: `0 ${theme.spaces[16]}` }}
                      />
                    </Box>
                    <ProjectAssetPolicyToggle
                      days={workspacePolicy.daysBeforeAssetAutoDeletion ?? 0}
                      isEnabled={
                        !(
                          lifecycleExemption ??
                          lifecyclePolicy.isExempt ??
                          false
                        )
                      }
                      onChange={(value: boolean) => {
                        setLifecycleExemption(!value);
                      }}
                    />
                  </>
                )}
              </ProjectSettingsSection>
            )}
            <WorkspacePicker
              isDisabled={true}
              selectedWorkspace={projectWorkspace}
              width={inputMeasure}
            />
            {showStorageSection && (
              <ProjectSettingsSection title={TEXT.storageSectionTitle}>
                <MountedStorageTypeSelect
                  disableUpgrade={storageUpgradeError}
                  isDisabled={
                    project.mountedStorageStatus === 'LOCKED' ||
                    !canManageProjectResourceControlPolicies
                  }
                  isFullSize={true}
                  setStatus={setMountedStorageStatus}
                  status={mountedStorageStatus}
                />
              </ProjectSettingsSection>
            )}
          </ContentColumn>
        </ContentContainer>
      </Box>
      <FooterContainer>
        <DialogActions>
          <Button2
            data-testid="workspaces-project-settings-modal--cancel-button"
            onPress={handleCancel}
          >
            {TEXT.cancel}
          </Button2>
          <Button2
            data-testid="workspaces-project-settings-modal--save-button"
            isDisabled={isSaveDisabled}
            onPress={handleSave}
            variant="primary"
          >
            {TEXT.save}
          </Button2>
        </DialogActions>
      </FooterContainer>
    </>
  );
};

export const ProjectSettingsModal = ({
  isShowing,
  onRequestClose,
  accountId,
  projectId,
  trackingSource,
  workspaceId,
  zIndex,
}: ProjectSettingsModalProps) => {
  const breakpoint = useActiveWindowBreakpoint();
  const [isStorageConfirmShowing, setIsStorageConfirmShowing] = useState(false);
  const performSaveRef = useRef<PerformSaveFunction | null>(null);

  const handleRequestStorageConfirm = useCallback(
    (performSave: PerformSaveFunction) => {
      performSaveRef.current = performSave;
      setIsStorageConfirmShowing(true);
    },
    []
  );

  const handleConfirmStorageSwitch = useCallback(() => {
    setIsStorageConfirmShowing(false);
    performSaveRef.current?.();
    performSaveRef.current = null;
  }, []);

  const handleCancelStorageSwitch = useCallback(() => {
    setIsStorageConfirmShowing(false);
    performSaveRef.current = null;
  }, []);

  return (
    <>
      <Dialog
        aria-labelledby={TEXT.projectSettings}
        enableClickOutsideToClose={false}
        isShowing={isShowing && !isStorageConfirmShowing}
        onRequestClose={onRequestClose}
        style={{
          minWidth: screenVariants[breakpoint]?.dialogWidth,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '600px',
          overflow: 'hidden',
        }}
        zIndex={zIndex}
      >
        <ProjectSettingsModalContent
          accountId={accountId}
          isShowing={isShowing}
          onRequestClose={onRequestClose}
          onRequestStorageConfirm={handleRequestStorageConfirm}
          projectId={projectId}
          trackingSource={trackingSource}
          workspaceId={workspaceId}
        />
      </Dialog>
      <DisableMountedStorageDialog
        confirmationWord={TEXT.moveProject.confirmationWord}
        confirmButtonText={TEXT.moveProject.buttonText}
        isShowing={isStorageConfirmShowing}
        onCancel={handleCancelStorageSwitch}
        onConfirm={handleConfirmStorageSwitch}
        title={TEXT.moveProject.title}
      />
    </>
  );
};
